import React from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { TutorialCard } from '@/components/tutorials/TutorialCard';
import { 
  Search, 
  BookOpen, 
  Server, 
  Code, 
  Shield, 
  Activity, 
  Brain, 
  Terminal, 
  Database, 
  Zap 
} from 'lucide-react';
import { getAllTutorials } from '@/utils/sample-data';

export default function HomePage() {
  const tutorials = getAllTutorials();
  const featuredTutorials = tutorials.slice(0, 3);

  return (
    <MainLayout>
      {/* Enhanced Hero Section with Animation */}
      <section className="pt-28 pb-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5 overflow-hidden">
        <div className="container max-w-6xl mx-auto relative">
          <div className="absolute -top-12 -right-12 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-24 -left-12 w-80 h-80 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{animationDuration: '8s', animationDelay: '1s'}} />
          
          <div className="relative z-10 text-center md:text-left md:flex items-center gap-8">
            <div className="md:w-3/5 animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Mate4Tech <span className="text-gradient bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Knowledge Hub</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto md:mx-0">
                Master modern technology with our in-depth guides spanning software development, 
                AI, automation, infrastructure, and DevOps.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row justify-center md:justify-start gap-4">
                <Link to="/tutorials">
                  <Button size="lg" className="w-full sm:w-auto animate-slide-in hover:scale-105 transition-transform">
                    <BookOpen className="mr-2 h-5 w-5" />
                    Browse Tutorials
                  </Button>
                </Link>
                <Link to="/categories">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto animate-slide-in hover:scale-105 transition-transform" style={{animationDelay: '200ms'}}>
                    <Search className="mr-2 h-5 w-5" />
                    Explore Categories
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="hidden md:block md:w-2/5 relative">
              <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
              <img 
                src="/lovable-uploads/eb455b8c-2030-4600-8cb7-85f47b38858e.png" 
                alt="Mate4Tech Knowledge Hub" 
                className="w-full max-w-md mx-auto md:mx-0 p-8 animate-float"
                style={{animation: 'float 6s ease-in-out infinite'}}
              />
            </div>
          </div>
          
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
            <div className="bg-background/50 backdrop-blur-sm border rounded-lg p-4 flex flex-col items-center text-center animate-fade-in hover:bg-background/80 transition-all hover:shadow-md">
              <div className="bg-primary/10 p-3 rounded-full">
                <Brain className="h-6 w-6 text-primary" />
              </div>
              <p className="mt-3 text-sm font-medium">AI & Machine Learning</p>
            </div>
            <div className="bg-background/50 backdrop-blur-sm border rounded-lg p-4 flex flex-col items-center text-center animate-fade-in hover:bg-background/80 transition-all hover:shadow-md" style={{animationDelay: '100ms'}}>
              <div className="bg-secondary/10 p-3 rounded-full">
                <Code className="h-6 w-6 text-secondary" />
              </div>
              <p className="mt-3 text-sm font-medium">Software Development</p>
            </div>
            <div className="bg-background/50 backdrop-blur-sm border rounded-lg p-4 flex flex-col items-center text-center animate-fade-in hover:bg-background/80 transition-all hover:shadow-md" style={{animationDelay: '200ms'}}>
              <div className="bg-primary/10 p-3 rounded-full">
                <Server className="h-6 w-6 text-primary" />
              </div>
              <p className="mt-3 text-sm font-medium">DevOps & Infrastructure</p>
            </div>
            <div className="bg-background/50 backdrop-blur-sm border rounded-lg p-4 flex flex-col items-center text-center animate-fade-in hover:bg-background/80 transition-all hover:shadow-md" style={{animationDelay: '300ms'}}>
              <div className="bg-secondary/10 p-3 rounded-full">
                <Zap className="h-6 w-6 text-secondary" />
              </div>
              <p className="mt-3 text-sm font-medium">Automation & Tooling</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tutorials Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-semibold">Featured Tutorials</h2>
            <Link to="/tutorials">
              <Button variant="ghost" size="sm">View all tutorials</Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredTutorials.map((tutorial, index) => (
              <div key={tutorial.id} className="animate-fade-in" style={{animationDelay: `${index * 100}ms`}}>
                <TutorialCard tutorial={tutorial} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expanded Categories Section with Modern UI */}
      <section className="py-16 px-4 bg-gradient-to-br from-muted/30 via-muted/50 to-muted/30">
        <div className="container mx-auto">
          <h2 className="text-2xl font-semibold mb-8 text-center">Browse by Topic</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/categories/infrastructure">
              <div className="bg-background/80 backdrop-blur-sm border rounded-lg p-6 flex items-center gap-4 hover:shadow-md transition-all hover:scale-105">
                <div className="bg-primary/10 p-3 rounded-full">
                  <Server className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Infrastructure & DevOps</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cloud, servers, CI/CD, and infrastructure management
                  </p>
                </div>
              </div>
            </Link>
            <Link to="/categories/development">
              <div className="bg-background/80 backdrop-blur-sm border rounded-lg p-6 flex items-center gap-4 hover:shadow-md transition-all hover:scale-105">
                <div className="bg-secondary/10 p-3 rounded-full">
                  <Code className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-medium">Software Development</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Frameworks, languages, and best practices
                  </p>
                </div>
              </div>
            </Link>
            <Link to="/categories/security">
              <div className="bg-background/80 backdrop-blur-sm border rounded-lg p-6 flex items-center gap-4 hover:shadow-md transition-all hover:scale-105">
                <div className="bg-destructive/10 p-3 rounded-full">
                  <Shield className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <h3 className="font-medium">Security</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cybersecurity, encryption, and secure practices
                  </p>
                </div>
              </div>
            </Link>
            <Link to="/categories/ai">
              <div className="bg-background/80 backdrop-blur-sm border rounded-lg p-6 flex items-center gap-4 hover:shadow-md transition-all hover:scale-105">
                <div className="bg-primary/10 p-3 rounded-full">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">AI & Machine Learning</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    AI tools, agentic AI, and machine learning pipelines
                  </p>
                </div>
              </div>
            </Link>
            <Link to="/categories/database">
              <div className="bg-background/80 backdrop-blur-sm border rounded-lg p-6 flex items-center gap-4 hover:shadow-md transition-all hover:scale-105">
                <div className="bg-secondary/10 p-3 rounded-full">
                  <Database className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-medium">Databases & Storage</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    SQL, NoSQL, data modeling and optimization
                  </p>
                </div>
              </div>
            </Link>
            <Link to="/categories/automation">
              <div className="bg-background/80 backdrop-blur-sm border rounded-lg p-6 flex items-center gap-4 hover:shadow-md transition-all hover:scale-105">
                <div className="bg-destructive/10 p-3 rounded-full">
                  <Zap className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <h3 className="font-medium">Automation</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Workflow automation, scripting, and tooling
                  </p>
                </div>
              </div>
            </Link>
          </div>
          <div className="mt-8 text-center">
            <Link to="/categories">
              <Button variant="outline" className="animate-pulse hover:animate-none">
                View All Categories
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10 z-0"></div>
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
        
        <div className="container max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-semibold animate-fade-in">Ready to level up your tech skills?</h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-in" style={{animationDelay: '100ms'}}>
            Our carefully crafted tutorials will guide you through complex technical concepts 
            across software development, AI, automation, infrastructure and more.
          </p>
          <div className="mt-8 animate-fade-in" style={{animationDelay: '200ms'}}>
            <Link to="/tutorials">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 hover:scale-105 transition-transform">
                Start Learning Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
